/**
 * EXPERIMENT COPY — do not confuse with the production file.
 *
 * Copied from `features/billing/components/ProcessingOverlay.tsx` and free to diverge. The
 * original still serves every other consumer (TeamDialog, AddOnConfigDialog, AddonRequiredDialog,
 * CreateSubAccountDialog); this one is the wizard's.
 *
 * Deliberately BACK to production's look: the real page stays in place, washed out behind a
 * translucent veil, with a centred spinner and the message under it.
 *
 * This file previously replaced the page with a skeleton of itself — an opaque `bg-muted` base,
 * grey placeholder cards, a veil over those, and the message in a pill. That was solving a real
 * problem (below) but it read as a different screen rather than as the current one working, and
 * a full-viewport dialog full of grey blocks is a worse thing to look at than the form you just
 * filled in.
 *
 * THE PROBLEM IT WAS SOLVING, now knowingly accepted:
 *
 * The wizard's `chargeSubscription` passes `refetchQueries: ['Me', 'GetCompanyInfo', …]`, and
 * `GetCompanyInfo` feeds `existingCard`. On a first-time purchase that query lands WHILE this
 * overlay is up, so a "Saved payment method" radio row materialises in the payment list
 * underneath it. Through a translucent veil that rearrangement is faintly visible, under the
 * words "do not close or refresh this page".
 *
 * At 90% opacity it is subtle, and the trade was judged worth it. If it turns out to read as a
 * fault, the fix is NOT to reintroduce the skeleton — it is to stop the payment list mutating
 * mid-charge. Note that simply freezing the list is also wrong: on a FAILED charge the overlay
 * lifts and the user is back on the payment step, where the newly saved card SHOULD be offered
 * for the retry. The row is correct; it is only being revealed at the wrong moment.
 */
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

type Props = {
  visible: boolean;
};

export function ProcessingOverlay({ visible }: Props) {
  const { t } = useTranslation();
  if (!visible) return null;

  return (
    /* `role="status"` + `aria-live` are the one thing kept from the skeleton version. The
       original had a spinner and nothing else, so a screen reader got no signal at all while the
       dialog sat frozen for the length of a Stripe charge. */
    <div
      role="status"
      aria-live="polite"
      className="absolute inset-0 z-30 flex flex-col items-center justify-center rounded-lg bg-background/90"
      data-testid="wizard-processing-overlay"
    >
      <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary" />
      {/* Two lines, not one sentence: the second is an instruction and carries more weight on its
          own row than trailing the first. */}
      <p className="px-6 text-center text-sm font-medium">
        {t('Your transaction is being processed.')}
      </p>
      <p className="px-6 text-center text-sm font-medium">
        {t('Please do not close or refresh this page.')}
      </p>
    </div>
  );
}
